'use client';

import React, { useState } from 'react';
import { TopNav } from '../TopNav';
import { MainNav, MainNavItemData } from '../MainNav';
import { SectionNav, SectionNavItemData, SectionNavBasket, SectionNavToggle } from '../SectionNav';
import { Sidebar, SidebarProps, MenuSectionData } from '../Sidebar';
import { Footer, FooterLinkSection, SocialLink } from '../Footer';
import { Breadcrumbs, BreadcrumbItem } from '../../molecules/Breadcrumbs';
import { LayoutContainer } from '../../atoms/LayoutContainer';
import { Flex } from '../../atoms/Flex';
import './Page.css';

export interface PageProps {
  /**
   * Whether the user is logged in
   */
  isLoggedIn?: boolean;
  /**
   * Whether to show the search bar in TopNav
   */
  showSearch?: boolean;
  /**
   * The www domain for nav/footer URLs (e.g., 'www.heartinternet.uk').
   * The empty string means same-origin: links render path-relative and stay
   * on the current host — what a presales host passes so its nav resolves
   * against whatever environment it runs on. The customer portal passes its
   * environment's presales domain (`presales_www_domain`), since www is a
   * different host from the portal on every environment.
   */
  wwwDomain?: string;
  /**
   * The customer-portal domain for account links (TopNav login/dropdown,
   * MainNav's mobile login). Defaults to the empty string — same-origin —
   * which is correct wherever the portal itself renders the page, on every
   * environment. A presales host passes its environment's customer domain,
   * since the portal is a different host there.
   */
  customerDomain?: string;

  /**
   * MainNav items data
   */
  mainNavItems?: MainNavItemData[];

  /**
   * Whether the mobile MainNav bar is fixed to the top of the viewport. Defaults
   * to `true` (the customer portal). Marketing pages (presales) pass `false` so
   * the bar scrolls away with the page, letting a sticky SectionNav pin in its
   * place. No effect on desktop, where the MainNav already scrolls.
   */
  mainNavMobileFixed?: boolean;

  /**
   * Section sub-navigation links (the green bar below MainNav). Omitted/empty ⇒
   * no SectionNav renders — it's only shown on pages within a section that has
   * one. (Customer doesn't pass this, so its chrome is unchanged.)
   */
  sectionNavItems?: SectionNavItemData[];
  /** Current path, so SectionNav can mark the active link. */
  sectionNavCurrentPath?: string;
  /** Accessible name for the SectionNav landmark (e.g. "Domain names"). */
  sectionNavAriaLabel?: string;
  /** Optional View Basket button on the SectionNav bar (below 1024 only). */
  sectionNavBasket?: SectionNavBasket;
  /** Optional labelled toggle on the SectionNav bar (all widths). */
  sectionNavToggle?: SectionNavToggle;

  /**
   * Breadcrumb items
   */
  breadcrumbs?: BreadcrumbItem[];

  /**
   * Sidebar menu sections
   */
  sidebarSections?: MenuSectionData[];
  /**
   * Whether to show the cart button in sidebar
   */
  showCart?: boolean;
  /**
   * Whether to show the resume purchase box
   */
  showResumePurchase?: boolean;
  /**
   * Resume purchase box data
   */
  resumePurchaseData?: SidebarProps['resumePurchaseData'];

  /**
   * Footer link sections
   */
  footerSections?: FooterLinkSection[];
  /**
   * Footer social links
   */
  footerSocialLinks?: SocialLink[];
  /**
   * Footer blog link
   */
  footerBlogLink?: { label: string; href: string };

  /**
   * Hide the sidebar and layout container, rendering children full-width.
   * Use for pre-login pages (e.g. RegisterPage) that manage their own layout.
   */
  hideSidebar?: boolean;

  /**
   * Apply the page-content CSS reset (the `.hi-ui-page--reset …` overrides in
   * pages.css that re-assert hi-ui typography/forms over a host page's legacy
   * global CSS). Defaults to `true` for the customer portal, which mounts hi-ui
   * into legacy TT2 pages. Clean hosts with no legacy CSS to fight (e.g. the
   * presales Next app) pass `false` so their own component styles aren't
   * out-specificity'd. Transitional — removable once customer is fully migrated
   * into the monorepo and the legacy CSS is gone.
   */
  cssReset?: boolean;

  /**
   * Page content (the page-specific component)
   */
  children: React.ReactNode;

  /**
   * Custom CSS classes
   */
  className?: string;
}

export const Page: React.FC<PageProps> = ({
  isLoggedIn = true,
  showSearch = true,
  wwwDomain = 'www.heartinternet.uk',
  customerDomain = '',
  mainNavItems = [],
  mainNavMobileFixed = true,
  sectionNavItems,
  sectionNavCurrentPath,
  sectionNavAriaLabel,
  sectionNavBasket,
  sectionNavToggle,
  breadcrumbs,
  sidebarSections = [],
  showCart = true,
  showResumePurchase = false,
  resumePurchaseData,
  footerSections = [],
  footerSocialLinks = [],
  footerBlogLink,
  hideSidebar = false,
  cssReset = true,
  children,
  className = '',
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pageClasses = `hi-ui hi-ui-page ${cssReset ? 'hi-ui-page--reset' : ''} ${className}`
    .replace(/\s+/g, ' ')
    .trim();

  const handleAccountClick = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleMenuToggle = (isOpen: boolean) => {
    // When hamburger menu opens, close the sidebar
    if (isOpen) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className={pageClasses}>
      {/* Top Navigation */}
      <TopNav
        isLoggedIn={isLoggedIn}
        showSearch={showSearch}
        wwwDomain={wwwDomain}
        customerDomain={customerDomain}
      />

      {/* Main Navigation */}
      <MainNav
        items={mainNavItems}
        isLoggedIn={isLoggedIn}
        wwwDomain={wwwDomain}
        customerDomain={customerDomain}
        isMobileFixed={mainNavMobileFixed}
        onAccountClick={handleAccountClick}
        onMenuToggle={handleMenuToggle}
        sidebarOpen={sidebarOpen}
      />

      {/* Section sub-navigation (optional — only on pages within a section) */}
      {sectionNavItems && sectionNavItems.length > 0 && (
        <SectionNav
          items={sectionNavItems}
          currentPath={sectionNavCurrentPath}
          ariaLabel={sectionNavAriaLabel}
          basket={sectionNavBasket}
          toggle={sectionNavToggle}
        />
      )}

      {/* Page Content Area */}
      {hideSidebar ? (
        <div className="hi-ui-page__container hi-ui-page__container--full-width">{children}</div>
      ) : (
        <LayoutContainer className="hi-ui-page__container">
          <Flex className="hi-ui-page__layout" gap="lg">
            {/* Main Content */}
            <Flex as="main" direction="column" className="hi-ui-page__content" flex="1">
              {breadcrumbs && breadcrumbs.length > 0 && (
                <Breadcrumbs items={breadcrumbs} className="hi-ui-page__breadcrumbs" />
              )}
              {children}
            </Flex>

            {/* Sidebar */}
            <Sidebar
              sections={sidebarSections}
              showCart={showCart}
              showResumePurchase={showResumePurchase}
              resumePurchaseData={resumePurchaseData}
              isOpen={sidebarOpen}
            />
          </Flex>
        </LayoutContainer>
      )}

      {/* Footer */}
      <Footer
        sections={footerSections}
        socialLinks={footerSocialLinks}
        blogLink={footerBlogLink}
        wwwDomain={wwwDomain}
      />
    </div>
  );
};
